import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type {
  FunnelInsightsApiClient,
  CreativeProviderSourceSyncProgress,
} from '../../api/insightsClient';
import type {
  CreativeReportEnrichmentItem,
  ProviderSourceReportEnrichmentItem,
  ProviderSourceReportMedia,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { CreativeReportEnrichmentPanel } from './CreativeReportEnrichment';

interface CreativePreviewPanelProps {
  exactItem?: CreativeReportEnrichmentItem;
  providerSourceItem?: ProviderSourceReportEnrichmentItem;
  exactLoading?: boolean;
  providerSourceLoading?: boolean;
  projectId?: string;
  associationWindow?: { from: string; to: string };
  onProviderSourceSynced?: () => void;
  sourceSyncClient?: Pick<
    FunnelInsightsApiClient,
    'preflightProviderCreativeSourceReportRow' | 'syncMetaCreativeSources' | 'getProviderCreativeSourceSync'
  >;
}

/**
 * One evidence-first preview surface with an explicit precedence contract:
 * exact Segmently export -> immutable provider-source mirror -> honest
 * unavailable state. Neither fallback can hide the decision's metric blocks.
 */
export function CreativePreviewPanel({
  exactItem,
  providerSourceItem,
  exactLoading = false,
  providerSourceLoading = false,
  projectId,
  associationWindow,
  onProviderSourceSynced,
  sourceSyncClient,
}: CreativePreviewPanelProps): React.ReactElement | null {
  if (exactItem?.enrichment) {
    return <CreativeReportEnrichmentPanel item={exactItem} />;
  }
  if (providerSourceItem?.source) {
    return <CreativeProviderSourcePanel item={providerSourceItem} />;
  }
  if (exactLoading || providerSourceLoading) {
    return (
      <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentLoading}>
        Loading the exact export or synced provider source… Performance evidence remains available below.
      </Alert>
    );
  }
  if (providerSourceItem) {
    return (
      <ProviderSourceUnavailable
        item={providerSourceItem}
        projectId={projectId}
        associationWindow={associationWindow}
        onProviderSourceSynced={onProviderSourceSynced}
        sourceSyncClient={sourceSyncClient}
      />
    );
  }
  if (exactItem) return <CreativeReportEnrichmentPanel item={exactItem} />;
  return null;
}

function ProviderSourceUnavailable({
  item,
  projectId,
  associationWindow,
  onProviderSourceSynced,
  sourceSyncClient,
}: {
  item: ProviderSourceReportEnrichmentItem;
  projectId?: string;
  associationWindow?: { from: string; to: string };
  onProviderSourceSynced?: () => void;
  sourceSyncClient?: Pick<
    FunnelInsightsApiClient,
    'preflightProviderCreativeSourceReportRow' | 'syncMetaCreativeSources' | 'getProviderCreativeSourceSync'
  >;
}): React.ReactElement {
  const client = sourceSyncClient;
  const [starting, setStarting] = React.useState(false);
  const [syncRunId, setSyncRunId] = React.useState<string | null>(null);
  const [progress, setProgress] = React.useState<CreativeProviderSourceSyncProgress | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const canSync = Boolean(client && projectId && associationWindow && item.providerAdId && !syncRunId && !starting);

  React.useEffect(() => {
    setStarting(false);
    setSyncRunId(null);
    setProgress(null);
    setError(null);
  }, [item.correlationKey]);

  React.useEffect(() => {
    if (!client || !projectId || !syncRunId) return undefined;
    let stopped = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const response = await client.getProviderCreativeSourceSync(projectId, syncRunId);
        if (stopped) return;
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Creative sync progress is unavailable.');
        }
        setProgress(response.data);
        if (response.data.status === 'completed') {
          if (response.data.outcome === 'complete' || response.data.outcome === 'partial_success') {
            onProviderSourceSynced?.();
          } else {
            setError('The creative sync completed without a usable source.');
          }
          return;
        }
        if (['failed', 'cancelled', 'needs_reauth', 'paused_quota'].includes(response.data.status)) {
          setError(response.data.attention?.message || `Creative sync needs attention (${humanize(response.data.status)}).`);
          setSyncRunId(null);
          return;
        }
        timeout = setTimeout(poll, 1_500);
      } catch (nextError) {
        if (!stopped) {
          setError(nextError instanceof Error ? nextError.message : String(nextError));
          setSyncRunId(null);
        }
      }
    };
    void poll();
    return () => {
      stopped = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [client, onProviderSourceSynced, projectId, syncRunId]);

  const startSync = async () => {
    if (!canSync || !client || !projectId || !associationWindow || !item.providerAdId) return;
    setStarting(true);
    setError(null);
    setProgress(null);
    try {
      const preflightResponse = await client.preflightProviderCreativeSourceReportRow({
        projectId,
        providerAccountId: item.providerAccountId,
        providerAdId: item.providerAdId,
        ...(item.providerCreativeId ? { expectedProviderCreativeId: item.providerCreativeId } : {}),
        associationWindow,
      });
      if (!preflightResponse.success || !preflightResponse.data?.preflight) {
        throw new Error(preflightResponse.error || 'This Meta creative could not be reviewed for sync.');
      }
      const preflight = preflightResponse.data.preflight;
      const startResponse = await client.syncMetaCreativeSources({
        projectId,
        preflightToken: preflight.preflightToken,
        idempotencyKey: `report-row:${preflight.scopeDigest}:${preflight.issuedAt}`,
      });
      if (!startResponse.success || !startResponse.data) {
        throw new Error(startResponse.error || 'This Meta creative sync could not be started.');
      }
      setSyncRunId(startResponse.data.syncRunId);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceUnavailable}>
        {`No synced Meta reference copy is available for this deployment (${humanize('reason' in item ? item.reason : 'unknown')}). Performance evidence remains available below.`}
      </Alert>
      {item.providerAdId ? (
        <Button
          variant="outlined"
          onClick={startSync}
          disabled={!canSync}
          startIcon={starting ? <CircularProgress size={16} /> : undefined}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceSync}
        >
          {starting ? 'Reviewing exact Meta ad…' : 'Sync this creative'}
        </Button>
      ) : (
        <Typography variant="caption" color="text.secondary">
          This report row has no exact ad identity, so Segmently will not guess which Meta creative to sync.
        </Typography>
      )}
      {(syncRunId || progress) && !error && (
        <Alert severity={progress?.status === 'completed' ? 'success' : 'info'} data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceSyncStatus}>
          {progress?.status === 'completed'
            ? 'Creative synced. Refreshing the reference copy in this report…'
            : `Creative sync is ${humanize(progress?.status ?? 'pending')}. Performance evidence remains available.`}
        </Alert>
      )}
      {error && (
        <Alert severity="warning" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceSyncError}>
          <Stack spacing={0.75} alignItems="flex-start">
            <span>{error}</span>
            {projectId && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Link href={`/project/${encodeURIComponent(projectId)}/integrations`}>Reconnect Meta</Link>
                <Link href={`/project/${encodeURIComponent(projectId)}/content-plan/creatives?view=import`}>
                  Open creative import
                </Link>
              </Stack>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

export function CreativeProviderSourceThumbnail({
  item,
}: {
  item?: ProviderSourceReportEnrichmentItem;
}): React.ReactElement | null {
  const media = primaryMedia(item);
  if (!item?.source || !media) return null;
  return (
    <ProviderMedia
      media={media}
      compact
      label={item.source.creativeName ?? item.binding?.adName ?? 'Synced Meta creative'}
      testId={CREATIVE_DECISIONS_TEST_IDS.providerSourceThumbnail(item.correlationKey)}
    />
  );
}

export function CreativeProviderSourcePanel({
  item,
}: {
  item: ProviderSourceReportEnrichmentItem;
}): React.ReactElement | null {
  const source = item.source;
  if (!source) return null;
  const capabilityIsLimited = source.capabilityState !== 'full_media'
    || source.media.state !== 'mirrored'
    || source.media.truncated;
  return (
    <Box
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSource}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Synced Meta reference copy</Typography>
          <Typography variant="caption" color="text.secondary">
            Read-only Segmently mirror of the provider creative observed for this deployment
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="flex-start">
          <Chip size="small" variant="outlined" label={source.presentationState.replaceAll('_', ' ')} />
          <Chip
            size="small"
            variant="outlined"
            color={capabilityIsLimited ? 'warning' : 'success'}
            label={source.capabilityState.replaceAll('_', ' ')}
          />
        </Stack>
      </Stack>

      {source.media.items.length > 0 ? (
        <Stack spacing={1} component="section" aria-label="Synced provider media">
          {source.media.items.map((media) => (
            <Box key={`${media.slotKey}:${media.id}`} sx={{ minWidth: 0 }}>
              {source.media.items.length > 1 && (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Chip size="small" variant="outlined" label={media.slotKey} />
                  <Chip size="small" variant="outlined" label={media.kind} />
                </Stack>
              )}
              <ProviderMedia
                media={media}
                compact={false}
                label={source.creativeName ?? item.binding?.adName ?? 'Synced Meta creative'}
                testId={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceMedia(media.id)}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourceMetadataOnly}>
          The provider metadata is synced, but no durable media mirror is available yet.
        </Alert>
      )}

      <Stack spacing={0.75} sx={{ mt: 1.25 }}>
        {source.creativeName && <CopyLine label="Creative name" value={source.creativeName} />}
        {source.copy.headline && <CopyLine label="Headline" value={source.copy.headline} />}
        {source.copy.primaryText && <CopyLine label="Primary text" value={source.copy.primaryText} />}
        {source.copy.description && <CopyLine label="Description" value={source.copy.description} />}
        {source.cta.type && <CopyLine label="CTA" value={source.cta.type.replaceAll('_', ' ')} />}
        {source.cta.destinationUrl && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Observed destination</Typography>
            <Link
              href={source.cta.destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {source.cta.destinationUrl}
            </Link>
          </Box>
        )}
      </Stack>

      {source.taxonomy && (
        <Paper variant="outlined" sx={{ mt: 1.25, p: 1.25 }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Creative taxonomy</Typography>
              <Chip
                size="small"
                color={source.taxonomy.state === 'reviewed' ? 'success' : source.taxonomy.state === 'stale' ? 'warning' : 'default'}
                label={source.taxonomy.state.replaceAll('_', ' ')}
              />
              {source.taxonomy.reviewRequired && <Chip size="small" color="warning" label="review required" />}
            </Stack>
            {source.taxonomy.topic && <CopyLine label="Topic" value={source.taxonomy.topic.label} />}
            {source.taxonomy.angle && <CopyLine label="Angle" value={source.taxonomy.angle.label} />}
            {source.taxonomy.hook && <CopyLine label="Hook" value={source.taxonomy.hook.label} />}
            {source.taxonomy.format && <CopyLine label="Format" value={source.taxonomy.format.label} />}
            {!source.taxonomy.topic && (
              <Typography variant="caption" color="text.secondary">
                {source.taxonomy.state === 'stale'
                  ? 'The source creative changed after taxonomy review. Re-analyze the current snapshot.'
                  : 'No current reviewed taxonomy is attached to this exact report snapshot.'}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {capabilityIsLimited && (
        <Alert severity="warning" sx={{ mt: 1.25 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProviderSourcePartial}>
          {providerCapabilitySummary(item)}
        </Alert>
      )}

      <Stack spacing={0.25} sx={{ mt: 1.25 }}>
        {item.binding && (
          <Typography variant="caption" color="text.secondary">
            {[
              item.binding.campaignName,
              item.binding.adSetName,
              item.binding.adName,
            ].filter(Boolean).join(' · ') || `Meta ad ${item.binding.providerAdId}`}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {`Synced ${new Date(source.lastSyncedAt).toLocaleString()} · ${source.media.mirroredCount}/${source.media.totalCount} media mirrored`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {source.sourceLanguage.locale
            ? `Observed language evidence: ${source.sourceLanguage.locale} (${source.sourceLanguage.origin.replaceAll('_', ' ')}). It is not treated as the deployment locale.`
            : 'No deployment locale is inferred from this provider source.'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This is our immutable reference copy, not the advertiser&apos;s original upload master. Meta/Facebook state is never edited from this report.
        </Typography>
      </Stack>
    </Box>
  );
}

function ProviderMedia({
  media,
  compact,
  label,
  testId,
}: {
  media: ProviderSourceReportMedia;
  compact: boolean;
  label: string;
  testId: string;
}): React.ReactElement {
  const sx: SxProps<Theme> = compact
    ? { width: 44, height: 44, borderRadius: 0.75, objectFit: 'cover', bgcolor: 'action.hover', flexShrink: 0 }
    : { width: '100%', maxHeight: 320, borderRadius: 1, objectFit: 'contain', bgcolor: 'action.hover' };
  if (media.kind === 'video') {
    return (
      <Box
        component="video"
        src={media.url}
        preload="metadata"
        controls={!compact}
        muted={compact}
        playsInline
        aria-label={label}
        data-testid={testId}
        sx={sx}
      />
    );
  }
  return (
    <Box
      component="img"
      src={media.url}
      loading="lazy"
      alt={label}
      data-testid={testId}
      sx={sx}
    />
  );
}

function primaryMedia(item?: ProviderSourceReportEnrichmentItem): ProviderSourceReportMedia | undefined {
  const media = item?.source?.media.items ?? [];
  return media.find((entry) => entry.kind === 'image' || entry.kind === 'video' || entry.kind === 'poster');
}

function CopyLine({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
  );
}

function providerCapabilitySummary(item: ProviderSourceReportEnrichmentItem): string {
  if (!item.source) return 'Provider source unavailable.';
  const source = item.source;
  const reasons = source.capabilityReasons.map(humanize).filter(Boolean);
  const media = source.media;
  const parts = [
    media.state === 'partial' ? 'Only part of the provider media is mirrored.' : undefined,
    media.state === 'provider_reference_only' ? 'Only provider metadata is available.' : undefined,
    media.truncated ? `Showing ${media.returnedCount} of ${media.totalCount} media items.` : undefined,
    ...reasons,
  ].filter(Boolean);
  return parts.join(' ') || 'This source has limited provider evidence.';
}

function humanize(value: string | undefined): string {
  return (value ?? 'unknown').replaceAll('_', ' ');
}
